/**
 * ProposalBuilderActionBar — bottom action footer for the ProposalBuilder.
 *
 * Phase-2 of the iter154 ProposalBuilder decomposition (after
 * ProposalEmailDialog in iter157). The footer was a self-contained
 * 100+ line JSX block that grew increasingly painful to edit inline
 * inside the 2,000+ line `Proposals.jsx`:
 *
 *   • Cancel / "Are you sure?" two-state confirmation
 *   • PDF download (saved proposal → server-rendered; unsaved → client)
 *   • Save / Create
 *   • Email Proposal (saved only)
 *   • Convert to WO (saved only)
 *   • Auto-save status indicator
 *   • "Back to Work Order" return banner (when launched from a WO)
 *
 * Why not split further: every action depends on `form` + `proposal`
 * state from the parent, plus the same handful of handlers. Splitting
 * each button into its own file would explode the prop bag without
 * meaningful win.
 *
 * State stays in the parent (`ProposalBuilder`): we only render JSX
 * and route clicks back through the callbacks. Zero internal state
 * beyond what the parent already owns.
 */
import { Button } from "@/components/ui/button";
import { BTN } from "@/components/common/btn";
import { Eye, Download, Save, Mail, FileText, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { updateProposal } from "@/lib/api";
import { downloadPdfUrl } from "@/lib/downloadBlob";
import { generateProposalPDF } from "@/components/proposals/generateProposalPDF";
import { calcProposalTotals } from "@/components/proposals/proposalUtils";

export function ProposalBuilderActionBar({
  // form state
  form,
  proposal,
  saving,
  totals,
  autoSaveStatus,
  autoSaveTimer,
  setAutoSaveStatus,
  // cancel-confirm state
  confirmCancel,
  setConfirmCancel,
  // dialog togglers
  setShowPreview,
  setShowEmailDialog,
  // iter249 — flush helpers wrapping the setters above so the server
  // has the latest form data BEFORE we open Preview / Email / PDF.
  openPreviewFlush,
  openEmailDialogFlush,
  flushSave,
  // contextual props
  clientList,
  sites,
  // callbacks
  onClose,
  onSave,
  onConvertToWO,
  returnToWO,
  onReturnToWO,
  handleSave,
}) {
  const navigate = useNavigate();
  const [confirmWon, setConfirmWon] = useState(false);
  // iter255 — Foundation sync mode for Mark-Won. Defaults to "skipped"
  // (in-app only) because production Foundation push is currently down.
  const [fdnSyncMode, setFdnSyncMode] = useState("skipped");

  const handleConfirmCancel = async () => {
    // If this proposal has an id (already saved), stamp it as
    // 'cancelled' on the server. Otherwise just discard the
    // unsaved draft. (Feb 22, 2026 bug fix — the cancel button
    // used to just close the dialog, leaving the proposal with
    // its previous status (even "won") stuck in history.)
    setConfirmCancel(false);
    if (form?.id) {
      try {
        // #236 (iter168): PUT /proposals/{id} requires the full ProposalCreate
        // payload (name + client_id are required) and validates `status` against
        // the ProposalStatus enum. Sending only `{ status: "cancelled" }` 422s.
        // Spread the existing form (mirrors the Restore-to-Draft button in
        // Proposals.jsx ~1961) so all required fields are present.
        await updateProposal(form.id, { ...form, status: "cancelled" });
        toast.success("Proposal cancelled");
      } catch (e) {
        toast.error(e.response?.data?.detail || "Cancel failed");
      }
    }
    onClose();
  };

  const handleConvert = async (opts = {}) => {
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    try {
      setAutoSaveStatus("Saving...");
      const t = calcProposalTotals(form.scopes, form.tax_rate, form.cc_fee_pct == null ? 0 : form.cc_fee_pct, form.discount_type || "flat", form.discount_value == null ? 0 : form.discount_value, form.tax_override);
      await onSave(
        { ...form, value: t.total, qualifications: form.qualifications.filter((q) => q.trim()) },
        true,
      );
      setAutoSaveStatus("");
    } catch {
      /* save failed, proceed anyway */
    }
    onConvertToWO(proposal, opts);
  };

  const handleUnsavedPdf = () => {
    try { generateProposalPDF(form, clientList, sites, totals); }
    catch (err) { toast.error("Failed to generate PDF: " + (err?.message || "unknown error")); }
  };

  return (
    <div className="flex justify-between items-center pt-2 border-t flex-wrap gap-2">
      <div className="flex items-center gap-3">
        {returnToWO ? (
          <Button
            type="button"
            size="sm"
            onClick={onReturnToWO}
            data-testid="btn-return-to-wo"
            className={BTN.nav}
          >
            &larr; Back to work order
          </Button>
        ) : !confirmCancel ? (
          <Button
            type="button"
            size="sm"
            onClick={() => setConfirmCancel(true)}
            data-testid="btn-cancel-proposal"
            className={BTN.nav}
          >
            Cancel
          </Button>
        ) : (
          <div className="flex items-center gap-2 animate-in fade-in duration-200">
            <span className="text-xs font-medium text-red-700 bg-red-50 px-2 py-1 rounded">Are you sure?</span>
            <Button
              type="button"
              size="sm"
              className={BTN.danger}
              onClick={handleConfirmCancel}
              data-testid="btn-confirm-cancel-proposal"
            >
              Yes
            </Button>
            <Button
              type="button"
              size="sm"
              className={BTN.nav}
              onClick={() => setConfirmCancel(false)}
              data-testid="btn-deny-cancel-proposal"
            >
              No
            </Button>
          </div>
        )}
        {autoSaveStatus && (
          <span className={`text-xs ${autoSaveStatus === "Save failed" ? "font-medium text-red-600" : autoSaveStatus === "All changes saved" ? "text-emerald-600" : "text-slate-400"}`} data-testid="auto-save-status">
            {autoSaveStatus}
          </span>
        )}
      </div>
      <div className="flex gap-2 flex-wrap">
        <Button
          type="button"
          size="sm"
          className={`${BTN.nav} px-2.5`}
          onClick={() => (openPreviewFlush ? openPreviewFlush() : setShowPreview(true))}
          title="View"
          aria-label="View"
          data-testid="btn-view-proposal"
        >
          <Eye className="w-4 h-4" />
        </Button>
        <Button
          type="button"
          size="sm"
          className={`${BTN.write} px-2.5`}
          onClick={handleSave}
          disabled={saving}
          title={saving ? "Saving..." : "Save"}
          aria-label="Save"
          data-testid="btn-save-proposal"
        >
          <Save className="w-4 h-4" />
        </Button>
        {proposal?.id ? (
          <Button
            type="button"
            size="sm"
            className={`${BTN.data} px-2.5`}
            onClick={async () => {
              try { if (flushSave) await flushSave(); } catch (e) { toast.error(e.message || "Save failed"); return; }
              downloadPdfUrl(`/proposals/${proposal.id}/pdf?download=1`, {
                suggestedName: `ABS_Proposal_${proposal.proposal_number || proposal.id}.pdf`,
                label: "proposal",
              });
            }}
            title="Download PDF"
            aria-label="Download PDF"
            data-testid="btn-download-proposal-pdf"
          >
            <Download className="w-4 h-4" />
          </Button>
        ) : (
          <Button
            type="button"
            size="sm"
            className={`${BTN.data} px-2.5`}
            onClick={handleUnsavedPdf}
            title="Download PDF"
            aria-label="Download PDF"
            data-testid="btn-download-proposal-pdf-unsaved"
          >
            <Download className="w-4 h-4" />
          </Button>
        )}
        {proposal && (
          <Button
            type="button"
            size="sm"
            className={`${BTN.data} px-2.5`}
            onClick={() => (openEmailDialogFlush ? openEmailDialogFlush() : setShowEmailDialog(true))}
            title="Email proposal"
            aria-label="Email proposal"
            data-testid="btn-email-proposal"
          >
            <Mail className="w-4 h-4" />
          </Button>
        )}
        {/* Convert to WO = the one "won" action: marks the proposal Won and creates the
            Work Order. The inline confirm carries the Foundation choice (in-app vs queued)
            that the old separate "Mark as Won" button used to own. */}
        {proposal && onConvertToWO && (
          form.status === "won" && form.converted_wo_id ? (
            // iter210 — once the proposal has been signed/converted, the
            // "Already Converted" pill becomes an actionable link that
            // deep-links to the WO under the client's Work Orders tab.
            <Button
              type="button"
              size="sm"
              className={BTN.nav}
              onClick={() => navigate("/clients", {
                state: {
                  openClientId: form.client_id,
                  openTab: "workorders",
                  openWorkOrderId: form.converted_wo_id,
                  returnTo: window.location.pathname + window.location.search,
                },
              })}
              data-testid="btn-open-linked-wo"
            >
              <FileText className="w-3.5 h-3.5 mr-1" />
              Work order {form.converted_wo_number || ""}
              <ArrowRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          ) : (form.status === "won" && !proposal?.won_without_wo) || form.status === "lost" ? (
            <Button type="button" size="sm" disabled className="bg-slate-300 text-slate-500 cursor-not-allowed" data-testid="btn-convert-wo-builder">
              <FileText className="w-3.5 h-3.5 mr-1" />{" "}{form.status === "won" ? "Already converted" : "Convert to WO"}
            </Button>
          ) : !confirmWon ? (
            <Button type="button" size="sm" className={BTN.write} onClick={() => setConfirmWon(true)} data-testid="btn-convert-wo-builder">
              <FileText className="w-3.5 h-3.5 mr-1" /> Convert to WO
            </Button>
          ) : (
            <div className="flex items-center gap-2 flex-wrap animate-in fade-in duration-200 bg-emerald-50 border border-emerald-200 rounded-lg px-2 py-1.5" data-testid="convert-wo-confirm">
              <span className="text-xs font-semibold text-emerald-800 whitespace-nowrap">Marks Won · creates the work order.</span>
              <div className="flex items-center gap-1 text-[11px]">
                <label className="flex items-center gap-1 cursor-pointer" title="Skips Foundation ERP push. Job lives inside the app only.">
                  <input type="radio" name="fdn-sync" checked={fdnSyncMode === "skipped"} onChange={() => setFdnSyncMode("skipped")} data-testid="convert-sync-skipped" />
                  In-app only
                </label>
                <label className="flex items-center gap-1 cursor-pointer" title="Queues the WO for Foundation push when the bridge is available.">
                  <input type="radio" name="fdn-sync" checked={fdnSyncMode === "queued"} onChange={() => setFdnSyncMode("queued")} data-testid="convert-sync-queued" />
                  Queue for Foundation
                </label>
              </div>
              <Button type="button" size="sm" className={BTN.write} onClick={() => { setConfirmWon(false); handleConvert({ foundation_sync_mode: fdnSyncMode }); }} data-testid="btn-confirm-convert-wo">
                Confirm
              </Button>
              <Button type="button" size="sm" className={BTN.nav} onClick={() => setConfirmWon(false)} data-testid="btn-deny-convert-wo">
                Cancel
              </Button>
            </div>
          )
        )}
      </div>
    </div>
  );
}

export default ProposalBuilderActionBar;
